// Shared between Community, Corporate, etc
// NOTE: Search for all usages of this file (ie shared.min.js) when changed to ensure ?v=XX is bumped where needed

const ANALYTICS_ERROR_LOGGING_STORAGE_KEY = 'rv.analyticsErrorLoggingEnabled';

function setAnalyticsErrorLogging(enabled) {
    localStorage.setItem(ANALYTICS_ERROR_LOGGING_STORAGE_KEY, Boolean(enabled).toString());
}

function logAnalyticsError(error) {
    if (localStorage.getItem(ANALYTICS_ERROR_LOGGING_STORAGE_KEY) === 'true') {
        console.error(error);
    }
    logToServer(error, null, 'Analytics tracking error', 'SEVERE');
}

/**
 * Logs an error to a remote error-handling endpoint.
 *
 * @param {Error|string} error - The error object or string to be logged.
 * @param {Object} info - Additional information related to the error context.
 * @param {string} [message=''] - Optional human-readable message to supplement the error log.
 * @param {'INFO'|'WARNING'|'SEVERE'} [level='INFO'] - Optional logging level indicating the severity of the error.
 */
function logToServer(error, info, message = '', level = 'INFO') {
    fetch(
        new Request('/rapi/error-handler', {
            method: 'POST',
            body: JSON.stringify({
                error: error?.stack ?? error?.message ?? error ?? null,
                info,
                message,
                level,
            }),
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        }),
    )
        .catch
        // Ignore. Kinda hard to log the failure to log.
        ();
}

// Very basic debounce functionality
function debounce(func, delayMillis) {
    let timeoutId;
    return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delayMillis);
    };
}

function generateUuid() {
    // Note: crypto.randomUUID() is not available in non-secure context (eg no SSL, most often seen in local development or QA/pre-QA).
    if (typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    const arr = new Uint8Array(16);
    crypto.getRandomValues(arr);
    return [...arr].map((b) => b.toString(16).padStart(2, '0')).join('');
}

//region visitdata custom code
const VISIT_DATA_UUID_STORAGE_KEY = 'vsd-uuid-key';
const VISIT_DATA_SESSION_STORAGE_KEY = 'vsd-key';
const THIRTY_MINUTES_MILLIS = 1_000 * 60 * 30;

const referralUrls = new Set(['apts.com', 'chatgpt.com', 'zillow', 'googlead']);

function refineVisitData(existingVisitData, newVisitData, newRawData) {
    let source = newVisitData.source;
    let medium = newVisitData.medium;

    if (newRawData.paid_url_data?.source != null && newRawData.paid_url_data?.medium != null) {
        // Explicitly trust paid_url_data source/medium values, if present.
        source = newRawData.paid_url_data.source;
        medium = newRawData.paid_url_data.medium;
    } else {
        // This tracks if internal links to other pages within the site trigger a "referral"
        const selfReferral = medium === 'referral' && source === newRawData.this_domain;
        if (selfReferral) {
            // Retain the original source/medium data in a self-referral scenario
            source = existingVisitData?.source ?? '(unknown)';
            medium = existingVisitData?.medium ?? '(unknown)';
        }

        const referringHostnameSearchParams =
            newRawData.referring_hostname == null
                ? new URLSearchParams()
                : new URL(newRawData.referring_hostname).searchParams;

        // Refine the source first, since medium refinement could depend on an accurate source.
        // The order of each refinement step is important because they rely on the precedence of the available signals.
        if (source === '(unknown)') {
            if (newRawData.organic_search_data?.source != null) {
                // Trust source/medium values organic search data
                source = newRawData.organic_search_data.source;
            } else if (
                referringHostnameSearchParams.size === 0 &&
                newRawData.referral_data?.source != null &&
                newRawData.this_domain != null &&
                newRawData.referral_data.source !== newRawData.this_domain
            ) {
                // If a referral with no query parameters in the referring hostname, and the referrer is NOT the same domain as the site, then trust the referrer's source
                source = newRawData.referral_data.source;
            } else if (referringHostnameSearchParams.has('utm_source')) {
                // Use the utm_source parameter from the referring hostname if it exists
                source = referringHostnameSearchParams.get('utm_source');
            }
        }

        if (medium === '(unknown)') {
            if (newRawData.organic_search_data?.medium != null) {
                // Trust source/medium values organic search data
                medium = newRawData.organic_search_data.medium;
            } else if (
                source === 'seo' &&
                newRawData.referring_hostname == null &&
                newRawData.query_string === '?utm_source=seo'
            ) {
                // If the source is seo, and that's the only UTM tag, and there's no referring hostname, then it's organic
                medium = 'organic';
            } else if (
                referringHostnameSearchParams.size === 0 &&
                newRawData.referral_data?.source != null &&
                newRawData.this_domain != null &&
                newRawData.referral_data.medium != null &&
                newRawData.referral_data.source !== newRawData.this_domain
            ) {
                // If a referral with no query parameters in the referring hostname, and the referrer is NOT the same domain as the site, then trust the referrer's medium
                medium = newRawData.referral_data.medium;
            } else if (
                referringHostnameSearchParams.size === 0 &&
                newRawData.referral_data?.source != null &&
                newRawData.referral_data.source === newRawData.this_domain
            ) {
                // If a referral with no query parameters in the referring hostname, and the referrer IS the same domain as the site, then assume it's direct traffic
                medium = '(none)';
            } else if (
                newRawData.referral_data?.source != null &&
                newRawData.referral_data.source === newRawData.this_domain &&
                referringHostnameSearchParams.size === 1 &&
                referringHostnameSearchParams.get('utm_campaign') === 'gmb'
            ) {
                // If a referral with utm_campaign=gmb as the ONLY query parameter in the referring hostname, and the referrer IS the same domain as the site, then assume it's organic traffic (set gmb as the source if (unknown))
                medium = 'organic';
                if (source === '(unknown)') {
                    source = 'gmb';
                }
            } else if (
                referringHostnameSearchParams.size > 0 &&
                (referringHostnameSearchParams.has('gclid') ||
                    referringHostnameSearchParams.has('gbraid') ||
                    referringHostnameSearchParams.has('wbraid') ||
                    referringHostnameSearchParams.has('gclsrc'))
            ) {
                // If the referring hostname contains any Google ad parameters, then assume it's a CPC traffic source (set google as the source)
                medium = 'cpc';
                source = 'google';
            } else if (referringHostnameSearchParams.has('dclid')) {
                // If the referring hostname contains the dclid Google ad parameter, then assume it's a CPM traffic source (set google as the source)
                medium = 'cpm';
                source = 'google';
            } else if (referringHostnameSearchParams.has('fbclid')) {
                // If the referring hostname contains any Facebook ad parameters, then assume it's a CPC traffic source (set facebook as the source)
                medium = 'cpc';
                source = 'facebook';
            } else if (referringHostnameSearchParams.has('msclkid')) {
                // If the referring hostname contains any Bing ad parameters, then assume it's a CPC traffic source (set bing as the source)
                medium = 'cpc';
                source = 'bing';
            } else if (referringHostnameSearchParams.has('utm_medium')) {
                // Use the utm_medium parameter from the referring hostname if it exists
                medium = referringHostnameSearchParams.get('utm_medium');
            } else if (
                referralUrls.has(source.toLowerCase()) ||
                referralUrls.has(referringHostnameSearchParams.get('utm_source')?.toLowerCase())
            ) {
                // Mark as referral for the predefined list of sources
                medium = 'referral';
            } else if (referringHostnameSearchParams.get('utm_source') === 'seo') {
                // Mark as organic for referring utm_source=seo--makes no assumptions about the source
                medium = 'organic';
            } else if (referringHostnameSearchParams.get('utm_knock') === 'seo') {
                // Mark as organic for referring utm_knock=seo
                // If the source is also (unknown), set it to knock
                medium = 'organic';
                if (source === '(unknown)') {
                    source = 'knock';
                }
            } else if (referringHostnameSearchParams.has('utm_knock')) {
                // Mark as referral for referring utm_knock=
                // If the source is also (unknown), set it to knock-CODE
                medium = 'referral';
                if (source === '(unknown)') {
                    source = 'knock-' + referringHostnameSearchParams.get('utm_knock');
                }
            }
        }

        // Standardize googlead to google for cpc medium.
        if (source === 'googlead' && medium === 'cpc') {
            source = 'google';
        }
    }

    // Update the visitData object with the refined source/medium values.
    newVisitData.source = source;
    newVisitData.medium = medium;
}

function shouldUpdateVisitData(existingVisitData, newVisitData) {
    // We either don't already have a source/medium, or the new source is different and NOT "direct" (eg, they came from some other source)
    return (
        existingVisitData?.source == null ||
        existingVisitData?.medium == null ||
        (newVisitData.source !== '(direct)' && newVisitData.source !== existingVisitData.source)
    );
}

function setVisitData() {
    visitData.setOption('cache', false); // No caching via visitData, since we need to update session source in certain scenarios

    const currentVisitData = visitData.get();
    const currentRawData = visitData.rawData();
    const now = Date.now();

    let visitorUuid = localStorage.getItem(VISIT_DATA_UUID_STORAGE_KEY);
    // If we don't have a visitor UUID yet, this is the first tracked visit.
    if (visitorUuid == null) {
        visitorUuid = generateUuid();
        localStorage.setItem(VISIT_DATA_UUID_STORAGE_KEY, visitorUuid);
    }

    // Using localStorage for "session" in order to persist more in-line with a first-party cookie (how GA4 does it).
    const sessionData = JSON.parse(localStorage.getItem(VISIT_DATA_SESSION_STORAGE_KEY)) ?? {};
    refineVisitData(sessionData, currentVisitData, currentRawData); // Handle self-referrals and '(unknown)' source/medium occurrences.
    const dataUpdateRequired = shouldUpdateVisitData(sessionData, currentVisitData);
    if (dataUpdateRequired || sessionData.lastHit == null || now - sessionData.lastHit > THIRTY_MINUTES_MILLIS) {
        // Either the first hit or it's been more than thirty minutes or there's a new non-direct source--set a new session UUID
        sessionData.uuid = generateUuid();
        Object.assign(sessionData, currentVisitData);
        sessionData.rawData = currentRawData;
    }
    // Always update the "last hit" time
    sessionData.lastHit = now;

    localStorage.setItem(VISIT_DATA_SESSION_STORAGE_KEY, JSON.stringify(sessionData));
    return {
        visitorUuid,
        sessionData,
    };
}

// 15-second debouncing so we don't continuously fire during scrolling, mouse moving, etc.
const debouncedSetVisitData = debounce(setVisitData, 15_000);

function initializeVisitData() {
    setVisitData(); // Initial visitData population.

    //region Handle various events for session keep-alive.
    window.addEventListener('scroll', debouncedSetVisitData, { passive: true });

    ['mousemove', 'keydown', 'click'].forEach((event) => document.addEventListener(event, debouncedSetVisitData));

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            // No need to use the debounced version here, since this event is not frequently triggered.
            setVisitData();
        }
    });
    //endregion
}

function submitVisitData(visitData, eventType) {
    if (['engaged_visit', 'highly_engaged_visit'].includes(eventType)) {
        fetch(
            new Request('/rapi/visit-data', {
                method: 'POST',
                body: JSON.stringify({ ...visitData, eventType }),
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            }),
        );
    }
}

//endregion
